import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import zhCN from './zh-CN.json';
import enUS from './en-US.json';
import esES from './es-ES.json';
import frFR from './fr-FR.json';
import { setDefaultNamesLanguage } from '../utils/defaultNames';

const resources = {
  'zh-CN': { translation: zhCN },
  'en-US': { translation: enUS },
  'es-ES': { translation: esES },
  'fr-FR': { translation: frFR },
};

// 允许的语言列表
const ALLOWED_LANGS = ['en-US', 'es-ES', 'fr-FR'];

// 根据浏览器语言获取目标语言（除法语、西班牙语外都返回英文）
const getTargetLanguage = (): string => {
  const browserLang = navigator.language;
  if (browserLang.startsWith('es')) return 'es-ES';
  if (browserLang.startsWith('fr')) return 'fr-FR';
  return 'en-US';
};

// 获取初始语言：只接受允许的语言，否则根据浏览器语言
const getInitialLanguage = (): string => {
  const stored = localStorage.getItem('i18nextLng');
  if (stored && ALLOWED_LANGS.includes(stored)) {
    return stored;
  }
  return getTargetLanguage();
};

// 页面标题映射
const pageTitleKeys: Record<string, string> = {
  '/': 'indoorStory.hero.title',
  '/login': 'auth.login.title',
  '/register': 'auth.register.title',
  '/maps': 'maps.title',
  '/dashboard': 'menu.dashboard',
  '/profile': 'profile.title',
  '/subscription': 'subscription.title',
  '/billing': 'menu.billingNamespace.pageTitle',
  '/notifications': 'menu.notificationsNamespace.pageTitle',
  '/support': 'menu.supportNamespace.pageTitle',
  '/sdk': 'menu.sdk',
};

// 预设的标题翻译（用于初始化，避免闪烁）
const presetTitles: Record<string, Record<string, string>> = {
  'en-US': {
    '/': 'From 2D maps to spatial intelligence',
    '/login': 'KiMap Platform',
    '/register': 'KiMap Platform',
    '/maps': 'My Online Maps',
    '/dashboard': 'Dashboard',
    '/profile': 'Profile Settings',
    '/subscription': 'Subscription Plans',
    '/billing': 'Billing Management',
    '/notifications': 'Notifications',
    '/support': 'Support Tickets',
    '/sdk': 'Kimap SDK',
  },
  'es-ES': {
    '/': 'De mapas 2D a inteligencia espacial',
    '/login': 'Plataforma KiMap',
    '/register': 'Plataforma KiMap',
    '/maps': 'Mis Mapas en Línea',
    '/dashboard': 'Panel de Control',
    '/profile': 'Configuración de Perfil',
    '/subscription': 'Planes de Suscripción',
    '/billing': 'Gestión de Facturación',
    '/notifications': 'Notificaciones',
    '/support': 'Tickets de Soporte',
    '/sdk': 'Kimap SDK',
  },
  'fr-FR': {
    '/': 'Des cartes 2D à l\'intelligence spatiale',
    '/login': 'Plateforme KiMap',
    '/register': 'Plateforme KiMap',
    '/maps': 'Mes Cartes en Ligne',
    '/dashboard': 'Tableau de Bord',
    '/profile': 'Paramètres du Profil',
    '/subscription': 'Forfaits d\'Abonnement',
    '/billing': 'Gestion de la Facturation',
    '/notifications': 'Notifications',
    '/support': 'Tickets de Support',
    '/sdk': 'Kimap SDK',
  },
};

// 获取预设标题
const getPresetTitle = (path: string, lang: string): string => {
  const langTitles = presetTitles[lang] || presetTitles['en-US'];
  return langTitles[path] || langTitles['/login'] || 'KiMap';
};

// 更新浏览器标签页标题
const updatePageTitle = (path: string) => {
  const titleKey = pageTitleKeys[path] || 'auth.login.title';
  const translatedTitle = i18n.t(titleKey);
  document.title = translatedTitle ? `${translatedTitle} - KiMap` : 'KiMap';
};

// 初始语言
const initialLang = getInitialLanguage();

// 立即设置初始标题（同步，避免闪烁）
document.title = `${getPresetTitle(window.location.pathname, initialLang)} - KiMap`;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLang,
    fallbackLng: () => 'en-US',
    supportedLngs: ['zh-CN', 'en-US', 'es-ES', 'fr-FR'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  })
  .then(() => {
    // 初始化时同步 defaultNames 语言
    setDefaultNamesLanguage(i18n.language);
    // 初始化后更新页面标题（使用翻译）
    updatePageTitle(window.location.pathname);
  });

// 监听语言变化，同步 defaultNames 和页面标题
i18n.on('languageChanged', (lng) => {
  setDefaultNamesLanguage(lng);
  updatePageTitle(window.location.pathname);
});

// 导出更新页面标题的函数，供路由切换时调用
export const setDocumentTitle = (path: string) => {
  updatePageTitle(path);
};

export { useTranslation } from 'react-i18next';
export default i18n;
